// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { Props, State } from './NavigationView.types.tsx';
import { withRefreshBlocker } from './withRefreshBlocker.tsx';
import { findViewBy, transformRemoteView, updateContainerState } from './updateContainerState.tsx';

/**
 * Describes how `loadView` resolved, so the caller can decide how to navigate to
 * the view:
 *  - `created`: the view was fetched and inserted; when inserted as manually
 *    created, the root container's synchronizer will load its data and route to
 *    it - the caller needs to do nothing.
 *  - `alreadyExisted`: the view was already present (e.g. a realtime event beat
 *    our mutation response). The synchronizer only auto-routes views it newly
 *    inserts, so the caller should navigate to the view itself.
 *  - `error`: loading failed; the caller should fall back to a hard navigation.
 */
export type LoadViewResolution =
	| { status: 'created' }
	| { status: 'alreadyExisted' }
	| { status: 'error'; error: Error };

export type LoadViewOptions = {
	/**
	 * If a section matches this server `id`, the view is nested into it; otherwise
	 * it is appended at the top level.
	 */
	parentSectionId?: string;
	/**
	 * When true, the inserted view is flagged `isManuallyCreated`, which makes the
	 * PolarisRootDataContainer synchronizer load its (prefilled) data into the
	 * scoped views store and route to it - the same path a manually created view
	 * uses, avoiding a full page reload. Defaults to `false` (hydrate the nav list
	 * only, no auto-route).
	 */
	isManuallyCreated?: boolean;
	/**
	 * Reports the outcome. Used instead of a return value because the
	 * refresh-blocker wrapper does not propagate return values.
	 */
	onFinish?: (resolution: LoadViewResolution) => void;
};

/**
 * Fetches a single view by its UUID and inserts it into the navigation store.
 *
 * Unlike `refreshViews`, this is neither debounced nor throttled, so the caller
 * can rely on the view being present immediately after `onFinish` is called.
 *
 * When `options.isManuallyCreated` is true, the inserted view is flagged so the
 * PolarisRootDataContainer synchronizer loads the view's (prefilled) data into
 * the scoped views store and routes to it - the same path a manually created
 * view uses - avoiding a full page reload. This is the behaviour used when
 * creating a view from a template.
 *
 * The outcome is reported via the optional `onFinish` callback rather than a
 * return value, because the refresh-blocker wrapper does not propagate return
 * values.
 */
export const loadView = (viewUUID: string, options?: LoadViewOptions): Action<State, Props> =>
	withRefreshBlocker(
		async (
			{ setState, getState },
			{ navigationRemote, cloudId, containerId, onFailure, onSuccess, onViewCreated },
		) => {
			const { parentSectionId, isManuallyCreated = false, onFinish } = options ?? {};

			if (
				!cloudId ||
				!containerId ||
				!navigationRemote ||
				getState().containers[containerId]?.isLoading ||
				!getState().containers[containerId]?.initialized
			) {
				// Can't load here (e.g. invoked outside the sidebar container, which is
				// the only one with `navigationRemote`). Report an error so the caller
				// can fall back to a hard navigation.
				onFinish?.({
					status: 'error',
					error: new Error('loadView: navigation container is not ready'),
				});
				return;
			}

			// A realtime "view created" event can arrive before our own mutation
			// response. If the view is already present we do NOT re-fetch and do NOT
			// re-flag it - the synchronizer only auto-routes views it newly inserts, so
			// it won't pick this one up. We simply report `alreadyExisted` so the caller
			// can navigate to the (already loaded) view itself.
			const existing = findViewBy(
				getState().containers[containerId],
				(view) => view.id === viewUUID,
			).view;

			if (existing) {
				onSuccess?.('loadView');
				onViewCreated?.(existing);
				onFinish?.({ status: 'alreadyExisted' });
				return;
			}

			try {
				const remoteView = await navigationRemote.fetchView(viewUUID);

				const containerState = getState().containers[containerId];
				if (!containerState) {
					const error = new Error('loadView: container disappeared during fetch');
					onFailure?.(error, 'loadView');
					onFinish?.({ status: 'error', error });
					return;
				}

				const newView = {
					...transformRemoteView(remoteView),
					isManuallyCreated,
				};

				// Placement: mirror `createView` - if a section matches `parentSectionId`
				// (by server `id`), nest the view into it; otherwise append at the
				// top-level end. The backend places the view via `sectionId` and assigns
				// a rank on the next fetch; this approximates that to avoid a visible jump.
				const parentSection = parentSectionId
					? containerState.views.find((view) => view?.id === parentSectionId)
					: undefined;

				if (parentSection) {
					setState(
						updateContainerState(getState(), containerId, {
							views: containerState.views.map((view) =>
								view.id === parentSection.id
									? { ...view, views: [...(view.views || []), newView] }
									: view,
							),
						}),
					);
				} else {
					setState(
						updateContainerState(getState(), containerId, {
							views: [...containerState.views, newView],
						}),
					);
				}

				onSuccess?.('loadView');

				const navView = findViewBy(
					getState().containers[containerId],
					(view) => view.id === viewUUID,
				).view;
				if (navView) {
					onViewCreated?.(navView);
				}

				onFinish?.({ status: 'created' });
			} catch (e: unknown) {
				const error = e instanceof Error ? e : new Error(`Unknown error: ${String(e)}`);
				onFailure?.(error, 'loadView');
				onFinish?.({ status: 'error', error });
			}
		},
	);
